
import React, { useEffect } from 'react';
import { X, CheckCircle2, ClipboardCheck, ExternalLink } from 'lucide-react';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  message: string;
  type: 'code' | 'link';
  content?: string;
}

export const Modal: React.FC<ModalProps> = ({ isOpen, onClose, title, message, type, content }) => {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => { document.body.style.overflow = 'unset'; };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-slate-950/80 backdrop-blur-md transition-opacity duration-300 animate-in fade-in"
        onClick={onClose}
      />
      
      {/* Modal Content */}
      <div className="relative w-full max-w-lg bg-slate-900 border border-white/10 rounded-[2.5rem] shadow-2xl overflow-hidden animate-in zoom-in-95 duration-300">
        <div className="absolute top-0 left-0 w-full h-1.5 bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500" />
        
        <button 
          onClick={onClose}
          className="absolute top-6 right-6 p-2 rounded-full bg-slate-800 text-slate-400 hover:text-white hover:bg-slate-700 transition-all active:scale-90"
        >
          <X size={20} />
        </button>

        <div className="p-8 sm:p-10 flex flex-col items-center text-center">
          <div className="mb-6 p-4 rounded-3xl bg-indigo-500/10 text-indigo-400">
            {type === 'code' ? <ClipboardCheck size={48} strokeWidth={1.5} /> : <CheckCircle2 size={48} strokeWidth={1.5} />}
          </div>

          <h2 className="text-2xl font-black italic tracking-tight text-white mb-2 uppercase">
            {title}
          </h2>
          <p className="text-slate-400 font-medium mb-8">
            {message}
          </p>

          {content && (
            <div className="w-full mb-8 relative">
              <div className="absolute -top-3 left-6 px-3 py-1 rounded-full bg-slate-800 border border-white/10 text-[10px] font-black uppercase tracking-widest text-slate-400">
                {type === 'code' ? 'HTML Snippet' : 'Share URL'}
              </div>
              <div className="w-full max-h-32 overflow-y-auto p-6 pt-8 rounded-3xl bg-slate-950 border border-white/5 text-left custom-scrollbar">
                <code className="text-xs font-mono-custom text-indigo-300 break-all whitespace-pre-wrap">
                  {content}
                </code>
              </div>
            </div>
          )}

          <button 
            onClick={onClose}
            className="w-full py-4 rounded-2xl bg-white text-slate-950 font-black tracking-widest hover:bg-indigo-50 transition-all active:scale-95 uppercase"
          >
            Continuer
          </button>
        </div>
      </div>
    </div>
  );
};
